import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import './auth.scss';
import { supabase } from '../../lib/supabase';
import { subscriptionService } from '../../services/subscription';
import { PhoneInput } from '../phone-input/PhoneInput';

export function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [phone, setPhone] = useState('+234');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const { signUp, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Register the user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            firstName: firstName,
            phone: phone,
          }
        }
      });

      if (authError) throw authError;

      if (authData.user) {
        // Check if user profile already exists
        const { data: existingProfile } = await supabase
          .from('user_profiles')
          .select('*')
          .eq('id', authData.user.id)
          .single();

        // Create user profile if it doesn't exist
        if (!existingProfile) {
          try {
            const { error: profileError } = await supabase
              .from('user_profiles')
              .insert([
                {
                  id: authData.user.id,
                  email: email,
                  first_name: firstName,
                  phone: phone,
                }
              ]);

            if (profileError && profileError.code !== '23505') {
              throw profileError;
            }
          } catch (profileErr) {
            // Continue with registration even if profile creation fails due to duplicate
          }
        }

        // Set user to trial status using the subscription service
        try {
          await subscriptionService.updateSubscription(email, 'trial');
        } catch (subscriptionError) {
          // Fallback: Direct insert into premium_users if the subscription service failed
          try {
            const now = new Date();

            // Check if user already exists in premium_users
            const { data: existingUser } = await supabase
              .from('premium_users')
              .select('*')
              .eq('email', email)
              .single();

            if (existingUser) {
              await supabase
                .from('premium_users')
                .update({
                  status: 'trial',
                  startDate: now.toISOString(),
                  endDate: new Date(now.getTime() + 86400000).toISOString(), // 1 day trial
                  autoRenew: false
                })
                .eq('email', email);
            } else {
              await supabase
                .from('premium_users')
                .insert([
                  {
                    email: email,
                    status: 'trial',
                    startDate: now.toISOString(),
                    endDate: new Date(now.getTime() + 86400000).toISOString(), // 1 day trial
                    autoRenew: false
                  }
                ]);
            }
          } catch (directError) {
            // Continue with registration even if setting trial status fails
          }
        }

        // Send welcome message
        const ADMIN_ID = process.env.REACT_APP_ADMIN_USER_ID;
        if (ADMIN_ID) {
          const welcomeMessage = {
            sender_id: ADMIN_ID,
            receiver_id: authData.user.id,
            content: "Welcome to SimPLAB! 👋 I'm Dr. George. Feel free to ask any questions or leave comments if you need help. I'm here to assist you!",
            read: false,
            created_at: new Date().toISOString(),
          };

          await supabase.from('messages').insert([welcomeMessage]);
        }

        setMessage('Registration successful! Please check your email to verify your account.');
        navigate('/');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Failed to register. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-header">
          <h1>Create account!</h1>
          <p className="tagline">Simulate. Learn. Pass 🚀</p>
        </div>

        {/*<div className="benefits-list">
          <h3>Why join SimplePLAB<sup>2</sup>?</h3>
          <ul>
            <li>✓ Practice with realistic PLAB2 cases</li>
            <li>✓ Track your performance</li>
            <li>✓ Get personalized feedback</li>
            <li>✓ Join hundreds of PLAB2 candidates preparing for success</li>
          </ul>
        </div>*/}

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}
          {message && <div className="success-message">{message}</div>}

          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              type="text"
              id="firstName"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              placeholder="eg John"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="your.email@example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Choose a strong password"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <PhoneInput
              id="phone"
              value={phone}
              onChange={setPhone}
              required
            />
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading ? 'Signing up...' : 'Sign Up'}
          </button>
        </form>

        <p className="auth-switch">
          Member?{' '}
          <Link to="/login" className="forgot-password-link">
            ➡️Sign In
          </Link>
        </p>
      </div>
    </div>
  );
} 